/**
 * Translations API Service
 * Handles translation management API calls
 */

import apiClient from './axios';
import { ApiResponse } from './types';

export interface TranslationContent {
  id: string;
  key: string;
  category: 'interface' | 'system' | 'form' | 'user-generated';
  sourceText: string;
  translations: {
    [language: string]: {
      text: string;
      status: 'draft' | 'review' | 'approved';
      lastModified: Date;
      modifiedBy: string;
    };
  };
  context?: string;
  characterLimit?: number;
}

export interface TranslationStats {
  language: string;
  totalKeys: number;
  translated: number;
  completionRate: number;
  pendingReview: number;
}

export interface TranslationKey {
  key: string;
  category: string;
  sourceText: string;
  context?: string;
  characterLimit?: number;
}

export const translationsApi = {
  /**
   * Get all translations with filters
   */
  getTranslations: async (params?: {
    locale?: string;
    category?: string;
    status?: string;
    search?: string;
    per_page?: number;
  }): Promise<ApiResponse<TranslationContent[]>> => {
    const response = await apiClient.get<ApiResponse<TranslationContent[]>>('/translations/list', { params });
    return response.data;
  },

  /**
   * Get translation statistics
   */
  getStats: async (locales?: string[]): Promise<ApiResponse<TranslationStats[]>> => {
    const params = locales ? { locales: locales.join(',') } : {};
    const response = await apiClient.get<ApiResponse<TranslationStats[]>>('/translations/stats', { params });
    return response.data;
  },

  /**
   * Get all translation keys grouped by category
   */
  getKeys: async (locale?: string): Promise<ApiResponse<Record<string, TranslationKey[]>>> => {
    const params = locale ? { locale } : {};
    const response = await apiClient.get<ApiResponse<Record<string, TranslationKey[]>>>('/translations/keys', { params });
    return response.data;
  },

  /**
   * Get a specific translation by key
   */
  getTranslation: async (key: string): Promise<ApiResponse<TranslationContent>> => {
    const response = await apiClient.get<ApiResponse<TranslationContent>>(`/translations/${key}`);
    return response.data;
  },

  /**
   * Create or update a translation
   */
  saveTranslation: async (data: {
    key: string;
    locale: string;
    value: string;
    category?: string;
    context?: string;
    characterLimit?: number;
    status?: string;
  }): Promise<ApiResponse<TranslationContent>> => {
    const response = await apiClient.post<ApiResponse<TranslationContent>>('/translations', data);
    return response.data;
  },

  /**
   * Update translation status
   */
  updateStatus: async (
    key: string,
    locale: string,
    status: 'draft' | 'review' | 'approved'
  ): Promise<ApiResponse<TranslationContent>> => {
    const response = await apiClient.put<ApiResponse<TranslationContent>>(
      `/translations/${key}/${locale}/status`,
      { status }
    );
    return response.data;
  },

  /**
   * Export translations for a locale
   */
  export: async (locale: string): Promise<ApiResponse<{ locale: string; translations: Record<string, string> }>> => {
    const response = await apiClient.get<ApiResponse<{ locale: string; translations: Record<string, string> }>>(
      `/translations/${locale}/export`
    );
    return response.data;
  },

  /**
   * Import translations
   */
  import: async (data: {
    locale: string;
    translations: Record<string, string>;
    category?: string;
  }): Promise<ApiResponse<{ imported: number }>> => {
    const response = await apiClient.post<ApiResponse<{ imported: number }>>('/translations/import', data);
    return response.data;
  },

  /**
   * Get translations for frontend (public endpoint)
   */
  getPublicTranslations: async (locale?: string): Promise<ApiResponse<Record<string, string>>> => {
    const params = locale ? { locale } : {};
    const response = await apiClient.get<ApiResponse<Record<string, string>>>('/translations', { params });
    return response.data;
  },
};

